import React from 'react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, PageLayout } from '@bloomneo/uikit';
import { Briefcase, FileText, MessageSquare, Shield } from 'lucide-react';
import { Header, Footer, SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { route, hasRole } from '../../../shared/utils';

const ClientPage: React.FC = () => {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  const canAccess = hasRole(user, ['client.basic', 'admin.tenant', 'admin.org', 'admin.system']);

  if (!canAccess) {
    return (
      <PageLayout>
        <SEO title="Access Denied" description="You don't have permission to access this page" />
        <Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-destructive" />
                  <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                  <p className="text-muted-foreground mb-4">
                    This is the client portal — it's only available to client accounts.
                  </p>
                  <Button asChild>
                    <a href={route('/dashboard')}>Back to Dashboard</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </PageLayout.Content>
        <Footer />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SEO
        title="Client Portal"
        description="Your client portal — projects, documents, and messages"
      />
      <Header />

      <PageLayout.Content>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Welcome, {user.name || user.email.split('@')[0]}
            </h1>
            <p className="text-muted-foreground">
              Your client portal — projects, documents, and messages in one place.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Briefcase className="h-5 w-5" />
                  Projects
                </CardTitle>
                <CardDescription>Active work in progress</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">No projects yet.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="h-5 w-5" />
                  Documents
                </CardTitle>
                <CardDescription>Shared files and deliverables</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">No documents yet.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="h-5 w-5" />
                  Messages
                </CardTitle>
                <CardDescription>Communication with your team</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">No messages yet.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageLayout.Content>

      <Footer />
    </PageLayout>
  );
};

export default ClientPage;
